import {
  AuditFieldLabels,
  AuditHistoryRecord,
  NormalizedAuditChange,
  formatAuditDate,
  getAuditChanges,
  getAuditStatusOption,
  isStatusAuditField,
} from "../../shared/auditHistory";

interface Props {
  records: object[];
  fieldLabels?: AuditFieldLabels;
  statusContext?: string;
  emptyMessage?: string;
}

const AuditChangesList: React.FC<Props> = ({
  records,
  fieldLabels,
  statusContext,
  emptyMessage = "Nenhuma alteração encontrada.",
}) => {
  const renderChangeValue = (
    change: NormalizedAuditChange,
    valueType: "previous" | "current"
  ) => {
    const formattedValue = valueType === "previous" ? change.previousValue : change.currentValue;
    const rawValue = valueType === "previous" ? change.previousRawValue : change.currentRawValue;
    const statusOption = isStatusAuditField(change.field)
      ? getAuditStatusOption(rawValue, statusContext)
      : undefined;

    if (!statusOption) {
      return (
        <span className={`text-sm break-words ${valueType === "current" ? "text-[#333] font-medium" : "text-[#666]"}`}>
          {formattedValue}
        </span>
      );
    }

    return (
      <div
        className="mt-1 inline-flex h-7 max-w-full items-center justify-center rounded-md px-3"
        style={{ backgroundColor: statusOption.color }}
      >
        <div
          className="mr-2 h-3 w-3 shrink-0 rounded-full"
          style={{
            border: "2px solid #FFF",
            backgroundColor: statusOption.icon || statusOption.color,
          }}
        />
        <span className="truncate text-xs text-white">
          {statusOption.label}
        </span>
      </div>
    );
  };

  const recordsWithChanges = records
    .map((record) => ({
      record,
      changes: getAuditChanges(record as AuditHistoryRecord, fieldLabels, statusContext),
    }))
    .filter((item) => item.changes.length > 0);

  if (recordsWithChanges.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-32 rounded-lg border border-dashed border-[#D8DDE3] bg-[#FAFAFA] p-6">
        <span className="text-sm text-[#666]">{emptyMessage}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {recordsWithChanges.map(({ record, changes }, recordIndex) => {
        const firstChange = changes[0];

        return (
          <div
            key={`${(record as AuditHistoryRecord).data_historico || "audit"}-${recordIndex}`}
            className="border border-[#E0E0E0] rounded-lg p-4 bg-[#FAFAFA]"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-3 pb-2 border-b border-[#E0E0E0]">
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-[#062D4E]">
                  Data: {formatAuditDate((record as AuditHistoryRecord).data_historico)}
                </span>
                {firstChange.identifierValue && (
                  <span className="text-xs text-[#888]">
                    {firstChange.identifierLabel}: <strong>{firstChange.identifierValue}</strong>
                  </span>
                )}
              </div>

              {(record as AuditHistoryRecord).usuario && (
                <span className="text-xs text-[#888]">
                  Usuário: <strong>{(record as AuditHistoryRecord).usuario}</strong>
                </span>
              )}
            </div>

            <div className="flex flex-col gap-3">
              {changes.map((change) => (
                <div key={`${change.field}-${change.previousValue}-${change.currentValue}`} className="bg-white border border-[#ECEFF3] rounded-md p-3">
                  <div className="mb-2">
                    <span className="text-xs text-[#999] font-medium">Campo alterado</span>
                    <p className="text-sm text-[#1E2121] font-semibold">{change.fieldLabel}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex flex-col">
                      <span className="text-xs text-[#999] font-medium">Anterior</span>
                      {renderChangeValue(change, "previous")}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-[#999] font-medium">Atual</span>
                      {renderChangeValue(change, "current")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AuditChangesList;
